// Pagar fatura do Reservei no visual do checkout novo (2026-10-20).
//
// Entra pela mesma rota de sempre (/checkout/cobranca/:id — link dos e-mails da
// Central, do painel de faturas do Reservei e da tela de troca quando a
// assinatura está pausada). A página só troca de visual quando a cobrança é de
// uma assinatura do Reservei; as regras de pagamento são as da API (trocar o
// meio no mesmo payment do Asaas, cartão cobrado na hora, Pix com QR).

import { useQuery } from '@tanstack/react-query';
import { useReducedMotion } from 'framer-motion';
import { ArrowRight, CircleAlert, CreditCard, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  cardSaveText,
  fetchChargeCheckoutStatus,
  payCharge,
  type ChargeCheckoutDetail,
  type ChargePayResult,
} from '../../lib/charge-checkout-api';
import { maskCep } from '../../lib/masks';
import { PixIcon } from '../checkout-public/branded-icons';
import {
  BlockedScreen,
  FieldError,
  Footer,
  Header,
  PixQrBlock,
  Price,
  RESERVEI_FALLBACK_URL,
  SelectionHighlight,
  SuccessMark,
  formatCardNumber,
  formatExpiry,
  formatPrice,
  isExpiryValid,
  onlyDigits,
  parsePixExpiration,
  safeUrl,
  splitExpiry,
  toQrSrc,
  useCheckoutPageMeta,
  useIsDesktop,
} from './shell';

/** Mesmo ritmo da tela de troca (mesma rota de status). */
export const CHARGE_POLL_INTERVAL_MS = 5_000;

type Method = 'pix' | 'credit_card';

interface ErrorShape {
  response?: { status?: number; data?: { error?: { code?: unknown; message?: unknown } } };
}

/** Desfecho de cada erro do POST /pay — o que a pessoa vê e se ainda dá pra pagar aqui. */
export function chargePayError(err: unknown): { message: string; final: boolean; alreadyPaid: boolean } {
  const response = (err as ErrorShape | null | undefined)?.response;
  if (!response) {
    return { message: 'Não conseguimos falar com o servidor. Confira sua internet e tente de novo.', final: false, alreadyPaid: false };
  }
  const code = typeof response.data?.error?.code === 'string' ? response.data.error.code : '';
  if (code === 'CHARGE_ALREADY_PAID') return { message: 'Esta fatura já está paga.', final: true, alreadyPaid: true };
  // O Asaas não respondeu e a cobrança pode ter passado: "tente de novo" levaria
  // a pagar duas vezes. A tela segue acompanhando e mostra "paga" quando cair.
  if (code === 'CHECKOUT_RESULT_UNKNOWN') {
    return {
      message: 'Estamos confirmando o seu pagamento com o banco. Não pague de novo: assim que confirmar, esta tela avisa.',
      final: true,
      alreadyPaid: false,
    };
  }
  if (code === 'CHARGE_NOT_PAYABLE') {
    return { message: 'Esta fatura não pode mais ser paga por aqui. Fale com o suporte do Reservei.', final: true, alreadyPaid: false };
  }
  if (code === 'CLIENT_MISSING_DOCUMENT') {
    return { message: 'Seu cadastro está sem CPF ou CNPJ, e o banco exige. Fale com o suporte do Reservei.', final: true, alreadyPaid: false };
  }
  if (code === 'ASAAS_PAYMENT_FAILED') {
    return { message: 'O pagamento não foi aprovado. Confira os dados do cartão ou pague com Pix. Nada foi cobrado.', final: false, alreadyPaid: false };
  }
  return { message: 'Não conseguimos processar o pagamento agora. Tente de novo em instantes.', final: false, alreadyPaid: false };
}

export function ReserveiChargeCheckout({
  chargeId,
  detail,
}: {
  chargeId: string;
  detail: ChargeCheckoutDetail;
}): JSX.Element {
  useCheckoutPageMeta('Pagar fatura · Reservei');
  const reducedMotion = useReducedMotion() !== false;
  const isDesktop = useIsDesktop();

  // Pix pré-selecionado (decisão do Wesley, 20/10) — igual à compra e à troca
  // do Reservei. Cartão fica a um clique; é ele que deixa a mensalidade seguinte
  // sair sozinha, e a página padrão (outros SaaS) continua abrindo no cartão.
  const [method, setMethod] = useState<Method>('pix');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [showCardErrors, setShowCardErrors] = useState(false);
  // Marcada por padrão (decisão do Wesley, 29/10): o aceite é a caixa visível
  // com o valor e o "cancele quando quiser" — desmarcar paga só esta fatura.
  const [saveCard, setSaveCard] = useState(true);
  const cardSaveOffer = detail.card_save_offer ?? null;
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [result, setResult] = useState<ChargePayResult | null>(null);
  const [failure, setFailure] = useState<{ message: string; final: boolean } | null>(null);
  const [alreadyPaid, setAlreadyPaid] = useState(detail.paid);

  const statusQ = useQuery({
    queryKey: ['charge-status', chargeId],
    queryFn: () => fetchChargeCheckoutStatus(chargeId),
    enabled: Boolean(result) && !result?.paid && !alreadyPaid,
    refetchInterval: (q) => (q.state.data?.paid ? false : CHARGE_POLL_INTERVAL_MS),
    retry: false,
  });
  const paid = alreadyPaid || Boolean(result?.paid) || Boolean(statusQ.data?.paid);

  const homeUrl = detail.saas?.url || RESERVEI_FALLBACK_URL;
  const backTarget = safeUrl('/app/planos', homeUrl);
  const links = { homeUrl, backTarget, helpUrl: safeUrl('/suporte', homeUrl) };
  const amount = Number(detail.valor) || 0;

  if (failure?.final && !alreadyPaid) {
    return <BlockedScreen links={links} title="Não deu para pagar por aqui" message={failure.message} />;
  }

  const cardDigits = onlyDigits(cardNumber);
  const cardErrors = {
    number: cardDigits.length < 13 ? 'Número do cartão incompleto.' : '',
    holder: cardName.trim().length < 2 ? 'Informe o nome como está no cartão.' : '',
    expiry: !isExpiryValid(cardExpiry) ? 'Validade inválida (MM/AA).' : '',
    cvv: cardCvv.length < 3 ? 'CVV com 3 ou 4 números.' : '',
  };
  const cardInvalid = Object.values(cardErrors).some(Boolean);
  const shown = (key: keyof typeof cardErrors): string => (showCardErrors ? cardErrors[key] : '');

  type Stage = 'choose' | 'pix' | 'card' | 'done';
  const stage: Stage = paid ? 'done' : result ? (result.payment_method === 'pix' ? 'pix' : 'card') : 'choose';

  async function pay(): Promise<void> {
    if (submittingRef.current) return;
    if (method === 'credit_card' && cardInvalid) {
      setShowCardErrors(true);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setFailure(null);
    const { month, year } = splitExpiry(cardExpiry);
    try {
      const r = await payCharge(chargeId, {
        payment_method: method,
        credit_card:
          method === 'credit_card'
            ? {
                number: cardDigits,
                holder_name: cardName.trim(),
                expiry_month: month,
                expiry_year: year,
                ccv: cardCvv,
                postal_code: onlyDigits(postalCode) || undefined,
                address_number: addressNumber.trim() || undefined,
              }
            : undefined,
        ...(method === 'credit_card' && cardSaveOffer ? { save_card: saveCard } : {}),
      });
      setResult(r);
    } catch (err) {
      const outcome = chargePayError(err);
      if (outcome.alreadyPaid) setAlreadyPaid(true);
      else setFailure({ message: outcome.message, final: outcome.final });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const cta = ((): { label: string; disabled: boolean; action: () => void } | null => {
    if (stage === 'done') return { label: 'Voltar ao Reservei', disabled: false, action: () => window.location.assign(backTarget) };
    if (stage !== 'choose') return null;
    if (submitting) return { label: 'Processando…', disabled: true, action: () => undefined };
    return {
      label: method === 'pix' ? 'Gerar Pix' : `Pagar ${formatPrice(amount)}`,
      disabled: false,
      action: () => void pay(),
    };
  })();
  const primaryButton = cta ? (
    <button
      type="button"
      className="reservei-checkout__dock-cta"
      disabled={cta.disabled}
      aria-busy={submitting || undefined}
      onClick={(event) => {
        if (event.detail > 1) return;
        cta.action();
      }}
    >
      {submitting && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
      {cta.label}
      {!submitting && <ArrowRight size={18} aria-hidden="true" />}
    </button>
  ) : null;
  const totalBlock = (
    <div className="reservei-checkout__dock-total">
      <div className="reservei-checkout__dock-total-value">
        <span>Total · {formatPrice(amount)}</span>
        <small>{paid ? 'fatura paga' : 'fatura em aberto'}</small>
      </div>
    </div>
  );

  return (
    <div className="reservei-checkout">
      <Header {...links} />

      <main className="reservei-checkout__main">
        <div className="reservei-checkout__layout">
          <section className="reservei-checkout__intro" aria-labelledby="rc-charge-title">
            <h1 id="rc-charge-title" tabIndex={-1} aria-label="Pague sua fatura">
              <span>Pague sua</span>
              <span>fatura</span>
            </h1>
            <p>Confira o valor e escolha como pagar.</p>
          </section>

          <aside className="reservei-checkout__summary" aria-label="Resumo da fatura">
            <div className="reservei-checkout__summary-panel">
              <p className="reservei-checkout__eyebrow">Resumo</p>
              <div className="reservei-checkout__summary-heading">
                <span className="reservei-checkout__calendar" aria-hidden="true">
                  <img src="/checkout/reservei/calendario.png" alt="" />
                </span>
                <div className="reservei-checkout__summary-details">
                  <h2>{detail.descricao}</h2>
                  <span className="reservei-checkout__summary-cycle-label">{detail.cliente_nome}</span>
                </div>
                <div className="reservei-checkout__summary-price">
                  <Price value={amount} suffix="" />
                </div>
              </div>
              <dl className="reservei-checkout__summary-rows">
                <div>
                  <dt>Situação</dt>
                  <dd>{paid ? 'Paga' : detail.status === 'vencida' ? 'Vencida' : 'Em aberto'}</dd>
                </div>
              </dl>
              <p className="reservei-checkout__summary-note">
                {cardSaveOffer
                  ? 'Pagando no cartão, você pode deixá-lo salvo para as próximas mensalidades. '
                  : ''}
                O acesso é liberado assim que o banco confirmar.
              </p>
            </div>

            <div className="reservei-checkout__support">
              <img src="/checkout/reservei/aceno.png" alt="" />
              <p>
                <span>Dúvidas na fatura?</span>
                <span>
                  <a className="reservei-checkout__inline-link" href={links.helpUrl} target="_blank" rel="noopener noreferrer">
                    Fale com o suporte do Reservei
                  </a>
                </span>
              </p>
            </div>
          </aside>

          <section className="reservei-checkout__form-column" aria-label="Pagamento da fatura">
            {stage === 'choose' && (
              <div className="reservei-checkout__form">
                <fieldset className="reservei-checkout__payment">
                  <legend>Pagamento</legend>
                  <div className="reservei-checkout__payment-options">
                    <label className={method === 'pix' ? 'is-selected' : ''}>
                      {method === 'pix' && <SelectionHighlight reducedMotion={reducedMotion} />}
                      <input
                        type="radio"
                        name="rc-charge-method"
                        value="pix"
                        checked={method === 'pix'}
                        onChange={() => setMethod('pix')}
                      />
                      <span className="reservei-checkout__radio" aria-hidden="true" />
                      <PixIcon size={22} aria-label="" />
                      <span>Pix</span>
                    </label>
                    <label className={method === 'credit_card' ? 'is-selected' : ''}>
                      {method === 'credit_card' && <SelectionHighlight reducedMotion={reducedMotion} />}
                      <input
                        aria-label="Cartão de crédito"
                        type="radio"
                        name="rc-charge-method"
                        value="credit_card"
                        checked={method === 'credit_card'}
                        onChange={() => setMethod('credit_card')}
                      />
                      <span className="reservei-checkout__radio" aria-hidden="true" />
                      <CreditCard size={22} aria-hidden="true" />
                      <span>
                        <span className="reservei-checkout__method-label-full">Cartão de crédito</span>
                        <span className="reservei-checkout__method-label-short">Cartão</span>
                      </span>
                    </label>
                  </div>
                  <p className="reservei-checkout__payment-helper">
                    {method === 'pix'
                      ? 'O QR Code aparece na próxima tela. A confirmação chega em segundos.'
                      : 'O cartão é cobrado agora, com confirmação do banco em instantes.'}
                  </p>

                  {method === 'credit_card' && (
                    <div className="reservei-checkout__card-form">
                      <div className="reservei-checkout__field">
                        <label htmlFor="rc-charge-card-number">Número do cartão</label>
                        <input
                          id="rc-charge-card-number"
                          inputMode="numeric"
                          autoComplete="cc-number"
                          value={cardNumber}
                          aria-invalid={Boolean(shown('number'))}
                          onChange={(event) => setCardNumber(formatCardNumber(event.target.value))}
                        />
                        <FieldError id="rc-charge-card-number-error" message={shown('number')} />
                      </div>
                      <div className="reservei-checkout__field">
                        <label htmlFor="rc-charge-card-name">Nome impresso no cartão</label>
                        <input
                          id="rc-charge-card-name"
                          autoComplete="cc-name"
                          value={cardName}
                          aria-invalid={Boolean(shown('holder'))}
                          onChange={(event) => setCardName(event.target.value)}
                        />
                        <FieldError id="rc-charge-card-name-error" message={shown('holder')} />
                      </div>
                      <div className="reservei-checkout__field-row">
                        <div className="reservei-checkout__field">
                          <label htmlFor="rc-charge-card-expiry">Validade</label>
                          <input
                            id="rc-charge-card-expiry"
                            inputMode="numeric"
                            autoComplete="cc-exp"
                            placeholder="MM/AA"
                            value={cardExpiry}
                            aria-invalid={Boolean(shown('expiry'))}
                            onChange={(event) => setCardExpiry(formatExpiry(event.target.value))}
                          />
                          <FieldError id="rc-charge-card-expiry-error" message={shown('expiry')} />
                        </div>
                        <div className="reservei-checkout__field">
                          <label htmlFor="rc-charge-card-cvv">CVV</label>
                          <input
                            id="rc-charge-card-cvv"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            value={cardCvv}
                            aria-invalid={Boolean(shown('cvv'))}
                            onChange={(event) => setCardCvv(onlyDigits(event.target.value).slice(0, 4))}
                          />
                          <FieldError id="rc-charge-card-cvv-error" message={shown('cvv')} />
                        </div>
                      </div>
                      <div className="reservei-checkout__field-row">
                        <div className="reservei-checkout__field">
                          <label htmlFor="rc-charge-card-postal">CEP do titular</label>
                          <input
                            id="rc-charge-card-postal"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            value={postalCode}
                            onChange={(event) => setPostalCode(maskCep(event.target.value))}
                          />
                        </div>
                        <div className="reservei-checkout__field">
                          <label htmlFor="rc-charge-card-address">Número do endereço</label>
                          <input
                            id="rc-charge-card-address"
                            maxLength={20}
                            value={addressNumber}
                            onChange={(event) => setAddressNumber(event.target.value)}
                          />
                        </div>
                      </div>
                      {cardSaveOffer && (
                        <label className="reservei-checkout__consent" htmlFor="rc-charge-save-card">
                          <input
                            id="rc-charge-save-card"
                            type="checkbox"
                            checked={saveCard}
                            onChange={(event) => setSaveCard(event.target.checked)}
                          />
                          <span>{cardSaveText(cardSaveOffer)}</span>
                        </label>
                      )}
                    </div>
                  )}
                </fieldset>

                {failure && (
                  <p className="reservei-checkout__alert" role="alert">
                    {failure.message}
                  </p>
                )}
              </div>
            )}

            {stage === 'pix' && result && (
              <div className="reservei-checkout__demo-stage reservei-checkout__demo-result reservei-checkout__demo-result--pix">
                <div className="reservei-checkout__pix-shell">
                  <div className="reservei-checkout__pix-status-row">
                    <span className="reservei-checkout__pix-status">
                      <span className="reservei-checkout__pix-status-dot" aria-hidden="true" />
                      Aguardando pagamento
                    </span>
                  </div>
                  <div className="reservei-checkout__pix-title">
                    <PixIcon size={24} aria-label="" />
                    <h2>Pague com Pix</h2>
                    <p>Abra o app do seu banco, escolha Pix e escaneie o QR Code ou cole o código.</p>
                  </div>
                  {result.pix_qr_image || result.pix_payload ? (
                    <PixQrBlock
                      qrSrc={toQrSrc(result.pix_qr_image)}
                      payload={result.pix_payload}
                      amount={amount > 0 ? amount : null}
                      summary={detail.descricao}
                      expiresAt={parsePixExpiration(result.pix_expiration)}
                      waitingText="Assim que o banco confirmar, esta tela avança sozinha e o acesso é liberado no Reservei."
                    />
                  ) : (
                    <p className="reservei-checkout__alert" role="alert">
                      Não conseguimos gerar o QR do Pix agora.
                      {result.payment_url ? (
                        <>
                          {' '}
                          <a className="reservei-checkout__inline-link" href={result.payment_url} target="_blank" rel="noopener noreferrer">
                            Abrir a página de pagamento
                          </a>
                          .
                        </>
                      ) : (
                        ' Recarregue a página e tente de novo.'
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            {stage === 'card' && (
              <div className="reservei-checkout__demo-stage reservei-checkout__demo-result reservei-checkout__demo-result--card" aria-live="polite">
                <Loader2 size={22} className="animate-spin" aria-hidden="true" />
                <div>
                  <strong>Confirmando o pagamento</strong>
                  <span>Enviamos o cartão para o banco. Em geral é na hora — assim que confirmar, esta tela atualiza. Não pague de novo.</span>
                </div>
              </div>
            )}

            {stage === 'done' && (
              <div className="reservei-checkout__demo-stage reservei-checkout__success-stage" aria-labelledby="rc-charge-done">
                <SuccessMark />
                <div className="reservei-checkout__success-copy">
                  <strong id="rc-charge-done">{result ? 'Pagamento confirmado' : 'Fatura já paga'}</strong>
                  <p className="reservei-checkout__success-qualification">
                    {result
                      ? 'Recebemos seu pagamento. Se o acesso estava pausado, ele volta sozinho em instantes.'
                      : 'Esta fatura já foi paga — não precisa pagar de novo.'}
                  </p>
                  <div className="reservei-checkout__success-summary" aria-label="Resumo do pagamento">
                    <span>
                      <b>Fatura</b>
                      {detail.descricao}
                    </span>
                    <span>
                      <b>Valor</b>
                      {formatPrice(amount)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {stage !== 'done' && stage !== 'choose' && (
              <p className="reservei-checkout__locked-note">
                <CircleAlert size={14} aria-hidden="true" /> Pode fechar esta página depois de pagar: a confirmação vem do
                banco e o Reservei atualiza sozinho.
              </p>
            )}

            {isDesktop && primaryButton && (
              <div className="reservei-checkout__inline-actions">
                {totalBlock}
                {primaryButton}
              </div>
            )}
          </section>
        </div>
      </main>

      {!isDesktop && (
        <div className="reservei-checkout__action-dock reservei-checkout__action-dock--simple">
          {totalBlock}
          {primaryButton}
        </div>
      )}

      <Footer />
    </div>
  );
}
